import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { ActionItem, ActionType } from '../../api/types';
import { useSaveAction } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { blankToNull, dateTimeInputValue, fromDateTimeInput, toDateTimeInput } from '../common/format';
import { useErrorMessage } from '../common/Common';

const TYPES: ActionType[] = ['call', 'email', 'meeting', 'note', 'task'];

/** Schedule vs Log (ADR-74). Both write the same Action; the difference is which date field is
 *  filled. Logging sets `completedAtUtc` to when the activity happened, which is what files it
 *  under the right date in the timeline instead of under today. */
type Mode = 'schedule' | 'log';

export function ActionFormDialog({
  open, customerId, opportunityId, action, onClose, onSaved,
}: {
  open: boolean;
  customerId: number;
  opportunityId: number | null;
  action?: ActionItem | null;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const describeError = useErrorMessage();

  const save = useSaveAction();
  const [mode, setMode] = useState<Mode>('schedule');
  const [type, setType] = useState<ActionType>('note');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [outcome, setOutcome] = useState('');
  const [touched, setTouched] = useState(false);

  const editing = Boolean(action);

  useEffect(() => {
    if (!open) return;
    // Editing keeps the single form it has always had: an existing action already knows whether
    // it is scheduled or completed, and switching modes on it would silently rewrite dates.
    setMode(action?.completedAtUtc ? 'log' : 'schedule');
    setType(action?.type ?? 'note');
    setSubject(action?.subject ?? '');
    setNotes(action?.notes ?? '');
    setDueAt(toDateTimeInput(action?.dueAtUtc ?? null));
    setOccurredAt(toDateTimeInput(action?.completedAtUtc ?? null));
    setOutcome(action?.outcome ?? '');
    setTouched(false);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action]);

  // A fresh Log form defaults to now, because "when did it happen" is almost always "just now".
  useEffect(() => {
    if (!open || editing) return;
    if (mode === 'log' && occurredAt === '') setOccurredAt(dateTimeInputValue(new Date()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, open, editing]);

  const isLog = mode === 'log';
  const subjectMissing = subject.trim().length === 0;
  const occurredMissing = isLog && occurredAt.trim().length === 0;
  const occurredInFuture = isLog && occurredAt !== ''
    && new Date(occurredAt).getTime() > Date.now() + 5 * 60 * 1000;
  const serverErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  const maxOccurred = dateTimeInputValue(new Date());

  const submit = async () => {
    setTouched(true);
    if (subjectMissing || occurredMissing || occurredInFuture) return;
    await save.mutateAsync({
      id: action?.id,
      body: {
        customerId,
        opportunityId: action ? action.opportunityId : opportunityId,
        type,
        subject: subject.trim(),
        notes: blankToNull(notes),
        // Editing keeps whatever due date the action already had. Dropping it because the form
        // is in Log mode would silently destroy it — the PUT is a full replace.
        dueAtUtc: isLog && !editing ? null : fromDateTimeInput(dueAt),
        completedAtUtc: isLog ? fromDateTimeInput(occurredAt) : null,
        outcome: isLog ? blankToNull(outcome) : null,
      },
    });
    onSaved?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{editing ? t('forms.action.editTitle') : t('forms.action.newTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {save.error && !Object.keys(serverErrors).length && (
            <Alert severity="error">{describeError(save.error)}</Alert>
          )}

          {!editing && (
            <Stack spacing={0.5}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={mode}
                onChange={(_, value: Mode | null) => value && setMode(value)}
              >
                <ToggleButton value="schedule" data-testid="action-mode-schedule">
                  <ScheduleIcon fontSize="small" sx={{ mr: 0.75 }} />
                  {t('forms.action.modeSchedule')}
                </ToggleButton>
                <ToggleButton value="log" data-testid="action-mode-log">
                  <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 0.75 }} />
                  {t('forms.action.modeLog')}
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                {isLog ? t('forms.action.logHint') : t('forms.action.scheduleHint')}
              </Typography>
            </Stack>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth
                select
                label={t('common.type')}
                value={type}
                onChange={(e) => setType(e.target.value as ActionType)}
                data-testid="action-type"
              >
                {TYPES.map((option) => (
                  <MenuItem key={option} value={option}>{t(`actionType.${option}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {(!isLog || editing) && (
              <Grid size={{ xs: 12, sm: 7 }}>
                <TextField
                  fullWidth
                  label={t('opportunity.due')}
                  type="datetime-local"
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'data-testid': 'action-due' }}
                />
              </Grid>
            )}
            {isLog && (
              <Grid size={{ xs: 12, sm: editing ? 12 : 7 }}>
                <TextField
                  fullWidth
                  required
                  label={t('forms.action.occurredOn')}
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(e) => setOccurredAt(e.target.value)}
                  onBlur={() => setTouched(true)}
                  error={touched && (occurredMissing || occurredInFuture)}
                  helperText={
                    touched && occurredInFuture
                      ? t('forms.action.occurredInFuture')
                      : touched && occurredMissing
                        ? t('forms.action.occurredRequired')
                        : serverErrors.completedAtUtc
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: maxOccurred, 'data-testid': 'action-occurred-at' }}
                />
              </Grid>
            )}
          </Grid>

          <TextField
            label={t('forms.action.subject')}
            required
            autoFocus
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onBlur={() => setTouched(true)}
            error={(touched && subjectMissing) || Boolean(serverErrors.subject)}
            helperText={touched && subjectMissing ? t('forms.action.subjectRequired') : serverErrors.subject}
            inputProps={{ dir: 'auto', maxLength: 200, 'data-testid': 'action-subject' }}
          />

          <TextField
            label={t('common.notes')}
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'action-notes' }}
          />

          {isLog && (
            <TextField
              label={t('forms.action.outcome')}
              multiline
              minRows={2}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              error={Boolean(serverErrors.outcome)}
              helperText={serverErrors.outcome ?? t('forms.action.outcomeHint')}
              inputProps={{ dir: 'auto', maxLength: 1000, 'data-testid': 'action-outcome' }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={save.isPending}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending} data-testid="save-action">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
